import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../../services/api';
import { usePipelineCostItems } from '../../credits/pipelineCost';
import { stripOutputContract } from './shared';
import { ChoiceCard, FlowCanvas, FlowHeader, InspectorHeader, InstructionTab, ToggleCard, type FlowEdge, type FlowNode, type FlowSource } from './FlowCanvas';

export type PhotoStepKind = 'image' | 'seo';
export interface PhotoPipelineStep {
  id: string;
  title: string;
  kind: PhotoStepKind;
  reference: 'original' | 'first' | 'prev';
  useBrand: boolean;
  useReference?: boolean;
  promptInstruction: string;
  status?: string;
  imageUrl?: string;
}
export interface PhotoPipeline {
  id: string;
  name: string;
  description?: string;
  isDefault?: boolean;
  steps: PhotoPipelineStep[];
}

const KIND_META: Record<PhotoStepKind, { icon: string; label: string; head: string; ring: string }> = {
  image: { icon: '📷', label: 'Foto', head: 'bg-indigo-600', ring: 'ring-indigo-300' },
  seo: { icon: '🔎', label: 'SEO', head: 'bg-amber-500', ring: 'ring-amber-300' },
};

export const photoStepChip = (s: PhotoPipelineStep) => ({ icon: KIND_META[s.kind]?.icon || '📷', label: s.title });

function uid() { return Math.random().toString(36).slice(2, 10); }

const SRC_PRODUCT = 'src:product';
const SRC_REFS = 'src:refs';
const C_PRODUCT = '#64748b';
const C_STEP = '#4f46e5';
const C_REFS = '#c026d3';

// De qual imagem a etapa parte, já resolvendo os casos em que a escolha não se aplica
// (ex.: "principal" na própria principal cai na foto original).
function effectiveReference(step: PhotoPipelineStep, steps: PhotoPipelineStep[]): { ref: 'original' | 'first' | 'prev'; fromId: string } {
  const idx = steps.findIndex(s => s.id === step.id);
  const firstImage = steps.find(s => s.kind === 'image');
  if (step.reference === 'prev' && steps[idx - 1]?.kind === 'image') return { ref: 'prev', fromId: steps[idx - 1].id };
  if (step.reference === 'first' && firstImage && firstImage.id !== step.id) return { ref: 'first', fromId: firstImage.id };
  return { ref: 'original', fromId: SRC_PRODUCT };
}

export function PhotoPipelineFlow({ pipeline, onSaved, onCloned, onDeleted, onDirtyChange }: {
  pipeline: PhotoPipeline;
  onSaved: (p: PhotoPipeline) => void;
  onCloned: (p: PhotoPipeline) => void;
  onDeleted: (id: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [draft, setDraft] = useState<PhotoPipeline>(pipeline);
  const [selectedId, setSelectedId] = useState<string | null>(pipeline.steps[0]?.id || null);
  const [tab, setTab] = useState<'basico' | 'refs' | 'instrucao'>('basico');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(pipeline);
    setSelectedId(pipeline.steps[0]?.id || null);
    setTab('basico');
  }, [pipeline.id]);

  const readonly = !!draft.isDefault;
  const dirty = JSON.stringify({ n: draft.name, d: draft.description || '', s: draft.steps }) !== JSON.stringify({ n: pipeline.name, d: pipeline.description || '', s: pipeline.steps });
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty]);

  const steps = draft.steps;
  const costItems = usePipelineCostItems('photo', steps);
  const selIdx = steps.findIndex(s => s.id === selectedId);
  const selected = selIdx >= 0 ? steps[selIdx] : null;
  const firstImageId = steps.find(s => s.kind === 'image')?.id;
  const usesRefs = steps.some(s => s.kind === 'image' && s.useReference);

  // ── Mutação das etapas ─────────────────────────────────────────────────────
  const patchStep = (idx: number, patch: Partial<PhotoPipelineStep>) =>
    setDraft(d => ({ ...d, steps: d.steps.map((s, i) => i === idx ? { ...s, ...patch } : s) }));

  // Etapas que partem do "resultado da etapa anterior" dependem da ordem.
  const usesPrevRef = (s?: PhotoPipelineStep) => !!s && s.kind === 'image' && s.reference === 'prev';
  const prevRefGuard = (affected: (PhotoPipelineStep | undefined)[], acao: string) => {
    const culprit = affected.find(usesPrevRef);
    if (!culprit) return false;
    toast.error(`"${culprit.title}" parte do resultado da etapa anterior. Antes de ${acao}, troque a entrada dela em Referências.`);
    return true;
  };

  const buildStep = (kind: PhotoStepKind): PhotoPipelineStep => ({
    id: uid(), kind, title: kind === 'seo' ? 'SEO das fotos' : 'Nova foto',
    reference: 'first', useBrand: false,
    promptInstruction: kind === 'seo'
      ? 'Gere títulos SEO e nomes de arquivo otimizados para cada imagem.'
      : 'Descreva a foto de produto desejada. Preserve fielmente o produto da referência.',
  });
  const insertStep = (kind: string, at: number) => {
    if (prevRefGuard([steps[at]], 'adicionar uma etapa aqui')) return;
    const step = buildStep(kind as PhotoStepKind);
    setDraft(d => { const next = [...d.steps]; next.splice(at, 0, step); return { ...d, steps: next }; });
    setSelectedId(step.id);
    setTab('instrucao');
  };
  const removeStep = (idx: number) => {
    if (prevRefGuard([steps[idx + 1]], 'remover esta etapa')) return;
    if (!window.confirm(`Remover a etapa "${steps[idx].title}"?`)) return;
    setDraft(d => ({ ...d, steps: d.steps.filter((_, i) => i !== idx) }));
    setSelectedId(steps[idx + 1]?.id || steps[idx - 1]?.id || null);
  };
  const moveStep = (from: number, to: number) => {
    if (to < 0 || to >= steps.length || from === to) return;
    if (prevRefGuard([steps[from], steps[from + 1], steps[to], steps[to + 1]], 'mudar a ordem')) return;
    setDraft(d => { const next = [...d.steps]; const [m] = next.splice(from, 1); next.splice(to, 0, m); return { ...d, steps: next }; });
  };

  // ── Persistência ───────────────────────────────────────────────────────────
  const save = async () => {
    if (!draft.name.trim()) { toast.error('Dê um nome ao estilo.'); return; }
    setSaving(true);
    try {
      const res = await api.updateEcommercePipeline(draft.id, { name: draft.name, description: draft.description, steps: draft.steps });
      toast.success('Estilo salvo');
      onSaved(res.pipeline);
    } catch (e: any) { toast.error(e.message || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };
  const clone = async () => {
    try { const res = await api.cloneEcommercePipeline(draft.id); toast.success('Cópia criada — agora é só editar'); onCloned(res.pipeline); }
    catch (e: any) { toast.error(e.message || 'Erro ao duplicar'); }
  };
  const remove = async () => {
    if (!window.confirm(`Excluir o estilo "${draft.name}"?`)) return;
    try { await api.deleteEcommercePipeline(draft.id); toast.success('Estilo excluído'); onDeleted(draft.id); }
    catch (e: any) { toast.error(e.message || 'Erro ao excluir'); }
  };

  // ── Grafo ──────────────────────────────────────────────────────────────────
  const sources: FlowSource[] = [
    { id: SRC_PRODUCT, icon: '📦', title: 'Foto do produto', subtitle: 'do catálogo ou enviada', borderCls: 'border-slate-300', labelCls: 'text-slate-400' },
    ...(usesRefs ? [{ id: SRC_REFS, icon: '🖼️', title: 'Referências', subtitle: 'fotos extras / anexadas', dashed: true, borderCls: 'border-fuchsia-300', labelCls: 'text-fuchsia-400' }] : []),
  ];
  const edges: FlowEdge[] = [];
  steps.forEach(s => {
    if (s.kind !== 'image') return;
    const { fromId } = effectiveReference(s, steps);
    edges.push({ from: fromId, to: s.id, color: fromId === SRC_PRODUCT ? C_PRODUCT : C_STEP, route: 'above' });
    if (s.useReference) edges.push({ from: SRC_REFS, to: s.id, color: C_REFS, route: 'below', dashed: true });
  });
  const nodes: FlowNode[] = steps.map(s => ({
    id: s.id, icon: KIND_META[s.kind].icon, kindLabel: KIND_META[s.kind].label,
    headCls: KIND_META[s.kind].head, ringCls: KIND_META[s.kind].ring,
    title: s.title, snippet: stripOutputContract(s.promptInstruction), approval: s.id === firstImageId,
    badges: (s.useBrand || s.useReference) ? <>
      {s.useBrand && <span className="rounded bg-slate-900 px-1.5 py-0.5 text-[9px] font-bold text-white">BRAND</span>}
      {s.useReference && <span className="rounded bg-fuchsia-100 px-1.5 py-0.5 text-[9px] font-bold text-fuchsia-700">+ REFS</span>}
    </> : undefined,
  }));

  const referenceLabel = (step: PhotoPipelineStep) => {
    if (step.kind === 'seo') return 'Todas as fotos geradas';
    const { ref, fromId } = effectiveReference(step, steps);
    const title = steps.find(s => s.id === fromId)?.title;
    return ref === 'prev' ? `Resultado de "${title}"` : ref === 'first' ? `Principal aprovada ("${title}")` : 'Foto original do produto';
  };

  return (
    <div className="space-y-4">
      <FlowHeader name={draft.name} description={draft.description || ''}
        onName={v => setDraft(d => ({ ...d, name: v }))} onDescription={v => setDraft(d => ({ ...d, description: v }))}
        readonly={readonly} dirty={dirty} saving={saving} costItems={costItems}
        onSave={save} onClone={clone} onRemove={remove} />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <FlowCanvas
          sources={sources} nodes={nodes} edges={edges}
          legend={[{ color: C_PRODUCT, label: 'foto original' }, { color: C_STEP, label: 'foto gerada' }, { color: C_REFS, label: 'referências', dashed: true }]}
          selectedId={selectedId} onSelect={setSelectedId} readonly={readonly} onMove={moveStep}
          insertOptions={[
            { key: 'image', icon: '📷', label: 'Etapa de foto', hint: 'Gera 1 foto do produto' },
            { key: 'seo', icon: '🔎', label: 'Etapa de SEO', hint: 'Títulos e nomes de arquivo' },
          ]}
          onInsert={insertStep}
        />

        {/* Painel da etapa selecionada */}
        <aside className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm xl:sticky xl:top-4 xl:self-start">
          {!selected ? (
            <p className="py-10 text-center text-sm text-slate-500">Selecione uma etapa no fluxo para ver os detalhes.</p>
          ) : (
            <div className="space-y-4">
              <InspectorHeader index={selIdx} title={selected.title} headCls={KIND_META[selected.kind].head} readonly={readonly}
                canLeft={selIdx > 0} canRight={selIdx < steps.length - 1}
                onLeft={() => moveStep(selIdx, selIdx - 1)} onRight={() => moveStep(selIdx, selIdx + 1)} onRemove={() => removeStep(selIdx)}
                tabs={[{ id: 'basico', label: 'Básico' }, { id: 'refs', label: 'Referências' }, { id: 'instrucao', label: 'Instrução' }]}
                tab={tab} onTab={setTab} />

              {tab === 'basico' && (
                <div className="space-y-3">
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-600">Nome da etapa</span>
                    <input value={selected.title} onChange={e => patchStep(selIdx, { title: e.target.value })} disabled={readonly}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50 disabled:text-slate-500" />
                  </label>
                  <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                    <p><b>Tipo:</b> {selected.kind === 'image' ? 'gera 1 foto (cobra 1 crédito de foto)' : 'gera título e nome de arquivo SEO para as fotos (sem custo de foto)'}</p>
                    <p className="mt-1"><b>Parte de:</b> {referenceLabel(selected)}</p>
                    {selected.id === firstImageId && <p className="mt-1 text-amber-700"><b>Pede aprovação:</b> é a foto principal — você aprova antes das demais serem geradas.</p>}
                  </div>
                  {selected.kind === 'image' && (
                    <ToggleCard checked={selected.useBrand} onChange={v => patchStep(selIdx, { useBrand: v })} disabled={readonly}
                      title="Aplicar minha marca" desc="Logo, cores e fontes da Brand entram nesta foto (a logo vai como imagem de referência)." />
                  )}
                </div>
              )}

              {tab === 'refs' && (
                selected.kind === 'seo' ? (
                  <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">A etapa de SEO lê os dados do produto e <b>todas as fotos geradas</b>. Não precisa escolher referência.</p>
                ) : (() => {
                  const effective = effectiveReference(selected, steps).ref;
                  const firstTitle = steps.find(s => s.id === firstImageId)?.title;
                  const options = [
                    { value: 'original' as const, title: 'Foto original do produto', desc: 'A foto do cadastro (ou a enviada). Mais fiel ao produto real.', show: true },
                    { value: 'first' as const, title: 'Foto principal aprovada', desc: `O resultado de "${firstTitle}" depois que você aprovar. Mantém o mesmo visual em todas.`, show: !!firstImageId && firstImageId !== selected.id },
                    { value: 'prev' as const, title: 'Resultado da etapa anterior', desc: selIdx > 0 ? `Encadeia com "${steps[selIdx - 1].title}". Mudar a ordem quebra essa ligação.` : '', show: (selIdx > 0 && steps[selIdx - 1].kind === 'image') || selected.reference === 'prev' },
                  ];
                  return (
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-slate-600">Esta foto parte de qual imagem?</p>
                      {options.filter(o => o.show).map(o => (
                        <ChoiceCard key={o.value} active={effective === o.value} title={o.title} desc={o.desc} disabled={readonly}
                          onClick={() => patchStep(selIdx, { reference: o.value })} />
                      ))}
                      <ToggleCard tone="fuchsia" checked={!!selected.useReference} onChange={v => patchStep(selIdx, { useReference: v })} disabled={readonly}
                        title="Usar também as fotos de referência"
                        desc="Produto do catálogo: as fotos extras do cadastro. Envio manual: as referências que você anexar (material, textura, ambiente)." />
                    </div>
                  );
                })()
              )}

              {tab === 'instrucao' && (
                <InstructionTab stepKey={selected.id} instruction={selected.promptInstruction} readonly={readonly}
                  onChange={v => patchStep(selIdx, { promptInstruction: v })}
                  runCopilot={async (direction) => (await api.assistEcommercePhotoPrompt({
                    stepKind: selected.kind, currentPrompt: selected.promptInstruction, direction,
                    reference: selected.reference, useReference: selected.useReference, stepTitle: selected.title,
                  })).prompt}
                  placeholder="Diga o que você quer. Ex.: produto na mão para mostrar escala, cozinha clara ao fundo"
                  help="É o roteiro que a IA segue para montar a foto." />
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
